import moment from 'moment-timezone';
import {green, grey, red, yellow} from '@mui/material/colors';

import HowToRegIcon from '@mui/icons-material/HowToReg';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';

import {ScheduleViewTypes} from "../domain/ScheduleViewTypes";
import {BOOKABLE_STATUS} from "../domain/BookableStatus";
import {MOMENT_UNIT_MINUTES} from "../domain/ShiftPlanConstants";
import {FULL_DAY_DATE_FORMAT, TWENTY_FOUR_HOUR_TIME_FORMAT} from "../config/DateTimeConstants";
import {SESSION_TYPE} from '../domain/SessionType';
import {formatDateAndTimeRange, formatMinutesAsHours} from './DateTimeRangeHelper';

export const PAID_CHECK_IN_WINDOW_START_MARGIN_MINUTES = 30;
export const PAID_CHECK_IN_WINDOW_END_MARGIN_MINUTES = 10;

export const DEFAULT_TIMESLOT_LENGTH_IN_SECONDS = 450;

export const AMENDMENT_DIRECTION = {
    CREDIT: 'CREDIT',
    DEBIT: 'DEBIT',
};

export const buildAmendmentShiftTimeRange = (amendment, timezone) => {
    const shiftStartTime = amendment?.shift?.performingFrom;
    const shiftEndTime = amendment?.shift?.to;

    if (!shiftStartTime || !shiftEndTime) {
        return '';
    }

    return formatDateAndTimeRange(shiftStartTime, shiftEndTime, timezone).time;
};

export const buildShiftServicesLabel = (amendment) => {
    const serviceKeycodes = Array.from(
        new Set(
            (amendment?.shift?.bookableServiceTypes || [])
                .map((bookableServiceType) => bookableServiceType?.serviceType?.keycode)
                .filter(Boolean)
        )
    );

    return serviceKeycodes.length > 0 ? ` (${serviceKeycodes.join(' + ')})` : '';
};

export const calculateNetAmendmentDurationInMinutes = (amendments = []) => amendments.reduce((totalMinutes, amendment) => {
    const amendmentMinutes = amendment?.durationInMinutes ?? 0;

    let netDurationInMinutes = totalMinutes;

    if (amendment?.direction === AMENDMENT_DIRECTION.DEBIT) {
        netDurationInMinutes -= amendmentMinutes;
    } else {
        netDurationInMinutes += amendmentMinutes;
    }

    return netDurationInMinutes;
}, 0);

export const calculateShiftDurationIncludingCheckInTimeInMinutes = (shift) => {
    const startTime = shift?.from;
    const endTime = shift?.to;

    if (!startTime || !endTime) {
        return 0;
    }

    return moment(endTime).diff(moment(startTime), 'minutes');
};

export const calculateShiftDurationExcludingCheckInTimeInMinutes = (shift) => {
    const startTime = shift?.performingFrom;
    const endTime = shift?.to;

    if (!startTime || !endTime) {
        return 0;
    }

    return moment(endTime).diff(moment(startTime), 'minutes');
};

export const buildShiftOptionLabel = (shift, timezone) => {
    if (!shift) {
        return null;
    }

    const shiftStartTime = shift.performingFrom;
    const shiftEndTime = shift.to;

    const dateLabel = moment.tz(shiftStartTime, timezone).format(FULL_DAY_DATE_FORMAT);
    const timeRangeLabel = formatDateAndTimeRange(shiftStartTime, shiftEndTime, timezone).time;
    const durationInMinutes = calculateShiftDurationExcludingCheckInTimeInMinutes(shift);
    const durationLabel = formatMinutesAsHours(durationInMinutes);

    return `${dateLabel}, ${timeRangeLabel} (${durationLabel}h)`;
};

export const calculateTotalShiftDurationIncludingCheckInTimeInMinutes = (shifts = []) => shifts.reduce(
    (totalMinutes, shift) => totalMinutes + calculateShiftDurationIncludingCheckInTimeInMinutes(shift),
    0
);

export const calculateTotalShiftDurationExcludingCheckInTimeInMinutes = (shifts = []) => shifts.reduce(
    (totalMinutes, shift) => totalMinutes + calculateShiftDurationExcludingCheckInTimeInMinutes(shift),
    0
);

export const calculateTotalForRowField = (rows = [], fieldName) => rows.reduce(
    (total, row) => total + (row?.[fieldName] || 0),
    0
);

export default function buildUserShiftOrTimeslotData(user, viewType = ScheduleViewTypes.SHIFT_ONLY, allServiceTypes) {

    const viewTypeDataHandlers = {
        [ScheduleViewTypes.DETAILED]: (performer) => performer.availability.flatMap(shift => {
            const updatedShiftWithVirtualTimeslots = generateVirtualTimeslotsForEmptyShiftTimes(shift, viewType);
            return updatedShiftWithVirtualTimeslots.sessions
                .map(session => buildTimeslotDataWithBookings(session, updatedShiftWithVirtualTimeslots));
        }),

        [ScheduleViewTypes.TIMESLOTS_ONLY]: (performer) => performer.availability.flatMap(shift => buildTimeslotData(shift)),

        [ScheduleViewTypes.SHIFT_ONLY]: (performer) => performer.availability.flatMap(shift => buildShiftData(shift)),
    };

    const fetchServiceTypes = (keycodes, shiftId) => {
        const allBookableServiceTypes = keycodes.flatMap(keycode => {
            const serviceType = allServiceTypes.find(type => type.keycode === keycode);
            return user.availability.flatMap(shift =>
                shift.bookableServiceTypes
                .filter(bookableServiceType => {
                    const keycodeMatches = bookableServiceType.serviceType.keycode === keycode;
                    const shiftMatches = shift.id === shiftId;
                    return keycodeMatches && shiftMatches;
                })
                .map(bookableServiceType => ({
                    ...serviceType,
                    isBookable: bookableServiceType.isBookable,
                    id: bookableServiceType.id,
                    startTime: bookableServiceType.startTime,
                    endTime: bookableServiceType.endTime
                }))
            );
        });

        // Deduplicate by unique `id`
        const dedupedServiceTypesMap = new Map();
        allBookableServiceTypes.forEach(serviceType => {
            if (!dedupedServiceTypesMap.has(serviceType.id)) {
                dedupedServiceTypesMap.set(serviceType.id, serviceType);
            }
        });

        return Array.from(dedupedServiceTypesMap.values()).sort((a, b) => a.keycode.localeCompare(b.keycode));
    };

    const buildShiftData = (shift) => {

        const serviceTypes = fetchServiceTypes(shift.bookableServiceTypes.map(servicetypes => servicetypes.serviceType.keycode), shift.id);

        return {
            id: shift.id,
            from: moment(shift.from),
            to: moment(shift.to),
            owner: user.id,
            displayName: `${user.displayName}`,
            availabilityId: shift.id,
            shiftStartTime: shift.from,
            shiftEndTime: shift.to,
            serviceType: serviceTypes.flat(),
            actualShiftStartTime: shift.performingFrom
        };
    };

    const buildTimeslotData = (shift) => {
        const serviceTypes = fetchServiceTypes(shift.bookableServiceTypes.map(servicetypes => servicetypes.serviceType.keycode), shift.id);
        const updatedShiftWithVirtualTimeslots = generateVirtualTimeslotsForEmptyShiftTimes(shift);

        return updatedShiftWithVirtualTimeslots.sessions.map(session => {

            const fromTime = moment(session.from);
            const toTime = moment(session.to);

            const formattedTimestamp = fromTime.format('HHmm');
            const uniqueId = `${shift.id}_${formattedTimestamp}`;
            const isBreakSession = session.sessionType === SESSION_TYPE.BREAK;
            const isTeachingSession = session.sessionType === SESSION_TYPE.TEACHING;
            const isBlockedSession = session.sessionType === SESSION_TYPE.BLOCKED;
            const isLockedSession = session.sessionType === SESSION_TYPE.TEACHING && session.isLocked;

            return {
                id: uniqueId,
                from: fromTime,
                actualShiftStartTime: shift.performingFrom,
                to: toTime,
                owner: user.id,
                displayName: `${user.displayName}`,
                availabilityId: shift.id,
                shiftStartTime: shift.from,
                shiftEndTime: shift.to,
                sessionType: session.sessionType,
                serviceType: serviceTypes.flat(),
                isUnbookable: isBlockedSession,
                isLocked: isLockedSession,
                isBreak: isBreakSession,
                hasSession: session.hasOwnProperty('hasSession') ? session.hasSession : isTeachingSession,
                session: session
            };
        });
    };

    const buildTimeslotDataWithBookings = (session, shift) => {

        const serviceTypes = fetchServiceTypes(shift.bookableServiceTypes.map(servicetypes => servicetypes.serviceType.keycode), shift.id);

        let checkInMinutes = 0;
        const isCheckInIncluded = isCheckInTimeIncluded(shift.sessions);

        if (isCheckInIncluded) {
            const checkInSession = shift.sessions.find(session => session.sessionType === SESSION_TYPE.CHECK_IN);
            checkInMinutes = moment(checkInSession.to).diff(moment(checkInSession.from), MOMENT_UNIT_MINUTES);
        }

        const timeslotsMappedWithSameSession = shift.sessions.filter(s => s.id === session.id);
        const earliestSessionStartTime = timeslotsMappedWithSameSession.reduce((earliest, current) =>
            moment(current.from).isBefore(earliest) ? moment(current.from) : earliest, moment(timeslotsMappedWithSameSession[0].from));
        const latestSessionEndTime = timeslotsMappedWithSameSession.reduce((latest, current) =>
            moment(current.to).isAfter(latest) ? moment(current.to) : latest, moment(timeslotsMappedWithSameSession[0].to));

        session.from = earliestSessionStartTime;
        session.to = latestSessionEndTime;
        session.isBookable = session.sessionType === SESSION_TYPE.TEACHING && !session.isLocked;
        session.isBreak = session.sessionType === SESSION_TYPE.BREAK;

        return {
            id: session.id,
            from: moment(session.from),
            actualShiftStartTime: shift.performingFrom,
            to: moment(session.to),
            owner: user.id,
            displayName: `${user.displayName}`,
            session: session,
            hasSession: session.contents?.length > 0 || false,
            isBooked: session.bookingsCount > 0,
            bookedSeats: session.bookingsCount,
            seatsCount: session.seatsCount,
            availabilityId: shift.id,
            availabilitySessionCount: countTeachingSessions(shift.sessions),
            isAnySessionAvailableInTheShift: isAnySessionAvailableInTheShift(shift.sessions),
            shiftStartTime: shift.from,
            shiftEndTime: shift.to,
            serviceType: serviceTypes.flat(),
            completedSessionCount: countCompletedSessions(shift.sessions),
            checkInTimeIncluded: isCheckInIncluded,
            checkInMinutes: checkInMinutes
        };
    };

    const generateVirtualTimeslotsForEmptyShiftTimes = (shift) => {
        shift.sessions.sort((sessionA, sessionB) => moment(sessionA.from).valueOf() - moment(sessionB.from).valueOf());

        let existingSessionAndVirtualTimeslotList = [];
        let currentTime = moment(shift.from);
        const shiftEndTime = moment(shift.to);

        while (currentTime.isBefore(shiftEndTime)) {
            let existingSession = shift.sessions.find(session => moment(session.from).isSame(currentTime));
            let nextTime = moment(currentTime).add(DEFAULT_TIMESLOT_LENGTH_IN_SECONDS, 'seconds');

            if (existingSession) {
                existingSessionAndVirtualTimeslotList.push(existingSession);
                nextTime = moment(existingSession.to);
            } else {
                const virtualTimeslot = {
                    id: `${shift.id}-${moment(currentTime).format('HHmm')}`,
                    from: currentTime.toISOString(),
                    to: nextTime.toISOString(),
                    sessionType: SESSION_TYPE.TEACHING,
                    isTeachingSession: true,
                    isBookable: true,
                    isLocked: false,
                    hasSession: false,
                    isBreak: false
                };

                existingSessionAndVirtualTimeslotList.push(virtualTimeslot);
            }

            currentTime = nextTime;
        }

        shift.sessions = existingSessionAndVirtualTimeslotList;
        return shift;
    }

    const countTeachingSessions = (sessions) => sessions.filter(session => session.sessionType !== SESSION_TYPE.CHECK_IN).length;
    const countCompletedSessions = (sessions) => sessions.filter(session => session.sessionType !== SESSION_TYPE.CHECK_IN && moment(session.to).isBefore()).length;
    const isAnySessionAvailableInTheShift = (sessions) => sessions.filter(session => session.seatsCount).length > 0;
    const isCheckInTimeIncluded = (sessions) => sessions.some(session => session.sessionType === SESSION_TYPE.CHECK_IN);

    return viewTypeDataHandlers[viewType](user);
}

export const createMultiColorBackground = (colors) => {
    return colors.length === 1
        ? colors[0]
        : `linear-gradient(to bottom, ${colors.map((color, index) => {
            return `${color} ${index * (100 / colors.length)}%, ${color} ${(index + 1) * (100 / colors.length)}%`;
        }).join(", ")})`;
};

export const createRepeatingPattern = (pattern, colors) => {
    if (colors.length === 1) {
        return pattern.replace(/timeRangeColor/g, colors[0]);
    } else {
        return `${pattern.replace(/timeRangeColor/g, 'transparent')}, ${createMultiColorBackground(colors)}`;
    }
};

export const hasDifferentKeycodes = (selectedServiceTypes, item) => {
    return selectedServiceTypes
        .slice()
        .sort((a, b) => a.keycode.localeCompare(b.keycode))
        .some((serviceType, index) =>
            serviceType.keycode !== item?.serviceType
                .slice()
                .sort((a, b) => a.keycode.localeCompare(b.keycode))[index].keycode
        );
};

export const hasDifferentBookableStatus = (bookableStatuses, item) => {
    return Object.keys(bookableStatuses).some(key => {
        const matchedService = item?.serviceType.find(type => key.includes(type.id));
        const initialStatus = matchedService?.isBookable
            ? BOOKABLE_STATUS.Bookable
            : BOOKABLE_STATUS.Unbookable;
        return bookableStatuses[key] !== initialStatus;
    });
};

export const hasDifferentTimestamps = (selectedServiceTypes, item, bookableServiceTypesTimestamps) => {
    return selectedServiceTypes.some(serviceType => {
        const serviceTypeId = serviceType.id;
        const matchingKey = Object.keys(bookableServiceTypesTimestamps).find(key => key.includes(serviceTypeId));
        const currentTimes = matchingKey ? bookableServiceTypesTimestamps[matchingKey] : {};
        const currentStartTime = currentTimes?.startTime ? moment(currentTimes.startTime) : null;
        const currentEndTime = currentTimes?.endTime ? moment(currentTimes.endTime) : null;
        const matchedService = item?.serviceType.find(type => type.id === serviceTypeId);
        const initialStartTime = matchedService?.startTime ? moment(matchedService.startTime) : null;
        const initialEndTime = matchedService?.endTime ? moment(matchedService.endTime) : null;
        if (!currentStartTime || !currentEndTime || !initialStartTime || !initialEndTime) {
            return true;
        }

        return !currentStartTime.isSame(initialStartTime) || !currentEndTime.isSame(initialEndTime);
    });
};

export const isTimestampInvalid = (timestamp, shiftStartTime, shiftEndTime) => {
    const startTime = moment(timestamp.startTime);
    const endTime = moment(timestamp.endTime);

    return (
      startTime.isBefore(moment(shiftStartTime)) ||
      endTime.isAfter(moment(shiftEndTime)) ||
      startTime.isSameOrAfter(endTime)
    );
}

export const hasInvalidTimestamp = (sortedTimestamps, shiftStartTime, shiftEndTime) => {
    return sortedTimestamps.some(timestamp =>
        isTimestampInvalid(timestamp, shiftStartTime, shiftEndTime)
    );
};

export const isTimeInvalid = (bookableServiceTypesTimestamps, shiftStartTime, shiftEndTime) => {
    const sortedTimestamps = Object.values(bookableServiceTypesTimestamps).sort((a, b) => {
        if (moment(a.startTime).isSame(moment(b.startTime))) {
            return moment(a.endTime).diff(moment(b.endTime));
        }

        return moment(a.startTime).diff(moment(b.startTime));
    });

    return hasInvalidTimestamp(sortedTimestamps, shiftStartTime, shiftEndTime)
    || doesShiftHaveAnyTimeThatIsntCoveredByABookableServiceType(sortedTimestamps, shiftStartTime, shiftEndTime);
};

export const doesShiftHaveAnyTimeThatIsntCoveredByABookableServiceType = (bookableServiceTypesTimestamps, shiftStartTime, shiftEndTime) => {
    if (Object.keys(bookableServiceTypesTimestamps).length === 0) {
      return true;
    }

    const intervals = Object.values(bookableServiceTypesTimestamps)
      .map(({ startTime, endTime }) => ({
        start: moment(startTime),
        end: moment(endTime),
      }))
      .sort((a, b) => a.start.diff(b.start));

    const mergedTimeIntervals = [];
    let current = intervals[0];

    for (let i = 1; i < intervals.length; i++) {
      const next = intervals[i];
      if (next.start.isSameOrBefore(current.end) && next.end.isAfter(current.end)) {
        current.end = next.end;
      } else if (next.start.isAfter(current.end)) {
        mergedTimeIntervals.push(current);
        current = next;
      }
    }

    mergedTimeIntervals.push(current);

    let coverageStart = moment(shiftStartTime);
    let uncoveredTimeExists = false;

    for (const interval of mergedTimeIntervals) {
      if (interval.start.isAfter(coverageStart)) {
        uncoveredTimeExists = true;
      }
      if (interval.end.isAfter(coverageStart)) {
        coverageStart = interval.end;
      }
    }

    if (coverageStart.isBefore(shiftEndTime)) {
      uncoveredTimeExists = true;
    }

    return uncoveredTimeExists;
};

export const isSameServiceTypesNotOverlapping = (bookableServiceTypesTimestamps) => {
    const timestampsWithServiceType = Object.entries(bookableServiceTypesTimestamps).map(([key, timestamps]) => ({
        serviceType: key.split("-")[0].trim(),
        ...timestamps,
    }));

    const groupedByServiceType = timestampsWithServiceType.reduce((accreditation, item) => {
        accreditation[item.serviceType] = accreditation[item.serviceType] || [];
        accreditation[item.serviceType].push(item);
        return accreditation;
    }, {});

    for (const timestamps of Object.values(groupedByServiceType)) {

        const sortedTimestamps = timestamps.sort((a, b) => moment(a.startTime).diff(moment(b.startTime)));

        for (let i = 0; i < sortedTimestamps.length - 1; i++) {
            const current = sortedTimestamps[i];
            const next = sortedTimestamps[i + 1];

            if (moment(current.endTime).isAfter(moment(next.startTime))) {
                return false;
            }
        }
    }

    return true;
};

export const isPerformerNotAccreditedForTimeslotWithBookings = (sessionStartTime, sessionEndTime, serviceTypes) =>
    serviceTypes.length === 0
    ||
    serviceTypes.every(serviceType => {
        const serviceStartTime = moment(serviceType.startTime);
        const serviceEndTime = moment(serviceType.endTime);

        return moment(sessionEndTime).isSameOrBefore(serviceStartTime) || moment(sessionStartTime).isSameOrAfter(serviceEndTime);
    });

export const isPerformerNotAccreditedForShift = (shiftStartTime, shiftEndTime, serviceTypes) => {

    return serviceTypes.some(serviceType => moment(shiftStartTime).isSameOrAfter(moment(serviceType.startTime)))
        &&
        serviceTypes.some(serviceType => moment(shiftEndTime).isSame(moment(serviceType.endTime)));
};

export const shiftPaidCheckInWindowStartTime = (shiftStartTime) => moment(shiftStartTime).subtract(PAID_CHECK_IN_WINDOW_START_MARGIN_MINUTES, MOMENT_UNIT_MINUTES);
export const shiftPaidCheckInWindowEndTime = (shiftStartTime) => moment(shiftStartTime).subtract(PAID_CHECK_IN_WINDOW_END_MARGIN_MINUTES, MOMENT_UNIT_MINUTES);

export const hasPerformerNotYetCheckedInButPaidCheckInWindowIsOpen = (shiftStartTime, performerCheckInTime) =>
    performerCheckInTime == null
    && moment().isBetween(shiftPaidCheckInWindowStartTime(shiftStartTime), shiftPaidCheckInWindowEndTime(shiftStartTime));

export const hasPerformerNotYetCheckedInAndPaidCheckInWindowIsOverButShiftHasNotEnded = (shiftStartTime, shiftEndTime, performerCheckInTime) =>
    performerCheckInTime == null
    && moment().isSameOrAfter(shiftPaidCheckInWindowEndTime(shiftStartTime))
    && moment().isSameOrBefore(shiftEndTime);

export const hasPerformerFailedToCheckedInAndShiftIsOver = (shiftEndTime, performerCheckInTime) =>
    performerCheckInTime == null
    && moment().isSameOrAfter(shiftEndTime);

export const hasPerformerCheckedInOnTime = (performerCheckInTime, shiftStartTime) =>
    performerCheckInTime != null
    && moment(performerCheckInTime).isSameOrBefore(shiftPaidCheckInWindowEndTime(shiftStartTime));

export const hasPerformerCheckedInLate = (performerCheckInTime, shiftStartTime) =>
    performerCheckInTime != null
    && moment(performerCheckInTime).isAfter(shiftPaidCheckInWindowEndTime(shiftStartTime));

export const buildPerformerCheckInDetails = (shiftStartTime, shiftEndTime, performerCheckInTime, timeFormat = TWENTY_FOUR_HOUR_TIME_FORMAT) => {

    const defaultDetails = {
        icon: <PersonIcon color={grey[900]}/>,
        tooltip: 'Not yet time for check in',
        color: 'white',
    };

    const performerCheckInStatusMap = [
        {
            condition: () => hasPerformerNotYetCheckedInButPaidCheckInWindowIsOpen(shiftStartTime, performerCheckInTime),
            icon: <PersonIcon color={grey[900]}/>,
            tooltip: 'Check-in window is open',
            color: yellow[700],
        },
        {
            condition: () => hasPerformerNotYetCheckedInAndPaidCheckInWindowIsOverButShiftHasNotEnded(shiftStartTime, shiftEndTime, performerCheckInTime),
            icon: <CloseIcon color={grey[900]}/>,
            tooltip: 'Not yet checked in',
            color: red[500],
        },
        {
            condition: () => hasPerformerFailedToCheckedInAndShiftIsOver(shiftEndTime, performerCheckInTime),
            icon: <CloseIcon color={grey[900]}/>,
            tooltip: 'Failed to check in',
            color: red[500],
        },
        {
            condition: () => hasPerformerCheckedInOnTime(performerCheckInTime, shiftStartTime),
            icon: <HowToRegIcon color={grey[900]}/>,
            tooltip: `Checked in on time, at ${moment(performerCheckInTime).format(timeFormat)}`,
            color: green[500],
        },
        {
            condition: () => hasPerformerCheckedInLate(performerCheckInTime, shiftStartTime),
            icon: <HowToRegIcon color={grey[900]}/>,
            tooltip: `Checked in at ${moment(performerCheckInTime).format(timeFormat)}`,
            color: red[300],
        }
    ];

    const matchedStatus = performerCheckInStatusMap.find(status => status.condition());

    return matchedStatus
        ? { icon: matchedStatus.icon, color: matchedStatus.color, tooltip: matchedStatus.tooltip }
        : defaultDetails;
};

export const determineShiftQueryName = (viewType) => {
    if (viewType === ScheduleViewTypes.SHIFT_ONLY) {
        return 'GetTeachingUsersAndTheirShiftData';
    } else if (viewType === ScheduleViewTypes.TIMESLOTS_ONLY) {
        return 'GetTeachingUsersAndTheirTimeslot';
    } else {
        return 'GetTeachingUsersAndTheirAvailability';
    }
};

export const getClearSessionsAlertMessage = (totalSessions, clearedSessions) => {
    const failed = totalSessions - clearedSessions;

    if (clearedSessions > 0 && failed === 0) {
        return {
            severity: 'success',
            message: `${clearedSessions} session${clearedSessions > 1 ? 's' : ''} moved successfully.`
        };
    }

    if (clearedSessions === 0 && failed > 0) {
        return {
            severity: 'warning',
            message: `${failed} session${failed > 1 ? 's' : ''} couldn't be moved automatically.`
        };
    }

    return {
        severity: 'warning',
        message: `${clearedSessions} session${clearedSessions > 1 ? 's' : ''} moved, ` +
            `${failed} couldn't be moved automatically.`
    };
};